const reachableValues = ({ optionValues, pool, optionName }) => {
  const reachable = new Set(
    pool
      .map((values) => values[optionName])
      .filter((value) => value !== undefined),
  );

  return optionValues.filter(({ name }) => reachable.has(name));
};

/**
 * Settles a selection against the variants that actually exist.
 *
 * The option the shopper touched last is the anchor: it keeps every one of its
 * values, so there is always a way back out of whatever they just picked. Every
 * other option is narrowed to the values that pair with the rest of the
 * selection.
 *
 * That takes two passes. The first settles the selection one option at a time,
 * each narrowed against the anchor *and* the options already settled above it:
 * narrowing against the anchor alone leaves two options each holding a value the
 * anchor can reach but that no variant pairs with each other — the combination
 * the form then reports as unavailable. The second pass is what the shopper
 * sees, and runs once the whole selection is settled, so no option is left
 * showing values that only the options below it in the first pass ruled out.
 *
 * @param {Array} options - `{ optionName, optionValues }`, already reduced to
 *   the values the form may render.
 * @param {Array} variants - the variants in play, storefront-shaped.
 * @param {Object} selection - optionName → optionValue.
 * @param {string|null} anchor - optionName changed last. Without one nothing is
 *   narrowed and the selection is handed back untouched, which is the state the
 *   form loads in.
 * @returns {{options: Array, selection: Object}} options in their input order
 *   with unreachable values dropped, and the selection moved onto what survived.
 */
export default function resolveOptionSelection({
  options,
  variants,
  selection,
  anchor,
}) {
  if (!anchor || !options.length) return { options, selection };

  const variantOptionValues = variants.map((variant) =>
    Object.fromEntries(
      variant.selectedOptions.map(({ name, value }) => [name, value]),
    ),
  );

  const optionNames = options.map(({ optionName }) => optionName);

  const order = [
    ...options.filter(({ optionName }) => optionName === anchor),
    ...options.filter(({ optionName }) => optionName !== anchor),
  ];

  const resolved = { ...selection };
  const resolvedValues = {};

  // Variants still in play with everything settled so far held fixed. Variants
  // that don't carry an option stay in, matching the variant lookup itself:
  // it reads a variant's own options and ignores selections beyond them, which
  // is what lets one group hold products with different options.
  let pool = variantOptionValues;

  order.forEach((option, index) => {
    const { optionName, optionValues } = option;

    let available = optionValues;

    if (index > 0) {
      const narrowed = reachableValues({ optionValues, pool, optionName });

      // Options a grouped product doesn't carry are reachable through nothing;
      // emptying them would leave the shopper nothing to pick.
      if (narrowed.length) available = narrowed;
    }

    if (
      available.length &&
      !available.some(({ name }) => name === resolved[optionName])
    ) {
      resolved[optionName] = available[0].name;
    }

    resolvedValues[optionName] = available;

    pool = pool.filter(
      (values) =>
        values[optionName] === undefined ||
        values[optionName] === resolved[optionName],
    );
  });

  order.slice(1).forEach(({ optionName }) => {
    const paired = variantOptionValues.filter((values) =>
      optionNames.every(
        (name) =>
          name === optionName ||
          values[name] === undefined ||
          values[name] === resolved[name],
      ),
    );

    const narrowed = reachableValues({
      optionValues: resolvedValues[optionName],
      pool: paired,
      optionName,
    });

    if (narrowed.length) resolvedValues[optionName] = narrowed;
  });

  return {
    options: options.map((option) => ({
      ...option,
      optionValues: resolvedValues[option.optionName],
    })),
    selection: resolved,
  };
}
